/**
 * Flexra Safety — MOQ (Minimum Order Quantity) Engine v3.0
 * ─────────────────────────────────────────────────────────
 * Rule:  price >= $75  →  MOQ = 1
 *        price <  $75  →  MOQ = ceil(100 / price_in_dollars)
 *
 * Covers:
 *  - Product page: badge, min qty, minus/plus lock, manual validation
 *  - Variant switch: re-calc on variant change
 *  - Add-to-Cart: form submit intercept (capture phase)
 *  - Fetch patch: intercepts /cart/add API call to enforce MOQ
 *  - Main cart page: checkout button + form submit validation
 *  - Side cart (halo-side-cart): minus lock, checkout intercept
 *  - Side cart AJAX re-render: MutationObserver auto-corrects qty + syncs server
 */

window.FlexraMOQ = (function () {

  /* ═══════════════════════════════════════════════════════
     1. CORE CALCULATOR
  ═══════════════════════════════════════════════════════ */
  function calcMOQ(priceCents) {
    const d = priceCents / 100;
    if (d <= 0) return 1;
    if (d >= 75) return 1;
    return Math.ceil(100 / d);
  }


  /* ═══════════════════════════════════════════════════════
     2. PRODUCT PAGE
  ═══════════════════════════════════════════════════════ */
  function initProductPage() {
    const input = document.querySelector('.productView-quantity .quantity__input');
    if (!input) return;

    const priceCents = parseInt(input.dataset.price || '0', 10);
    const moq = calcMOQ(priceCents);

    applyToInput(input, moq);
    renderBadge(input, moq);
    bindButtons(input, moq);
    bindManual(input, moq);
  }

  function applyToInput(input, moq) {
    input.setAttribute('min', moq);
    if (parseInt(input.value, 10) < moq) input.value = moq;
    input.dataset.moq = moq;
  }

  function renderBadge(input, moq) {
    const wrap = input.closest('.productView-quantity');
    if (!wrap) return;
    wrap.querySelectorAll('.flexra-moq-badge').forEach(el => el.remove());
    if (moq <= 1) return;
    const b = document.createElement('div');
    b.className = 'flexra-moq-badge';
    b.innerHTML = `<span class="flexra-moq-icon">&#9888;</span>&nbsp;Minimum Order: <strong>${moq} unit${moq > 1 ? 's' : ''}</strong>`;
    wrap.insertBefore(b, wrap.querySelector('.quantity__container'));
  }

  function bindButtons(input, moq) {
    const wrap = input.closest('.quantity__group');
    if (!wrap) return;

    const minus = wrap.querySelector('.minus.btn-quantity');
    const plus  = wrap.querySelector('.plus.btn-quantity');

    if (minus) {
      const newMinus = minus.cloneNode(true);
      minus.parentNode.replaceChild(newMinus, minus);
      newMinus.addEventListener('click', function () {
        const cur = parseInt(input.value, 10) || moq;
        if (cur - 1 < moq) {
          showPdpErr(input, `Minimum order quantity is ${moq} unit${moq > 1 ? 's' : ''}.`);
          input.value = moq;
        } else {
          clearPdpErr(input);
          input.value = cur - 1;
        }
      });
    }

    if (plus) {
      const newPlus = plus.cloneNode(true);
      plus.parentNode.replaceChild(newPlus, plus);
      newPlus.addEventListener('click', function () {
        clearPdpErr(input);
        input.value = (parseInt(input.value, 10) || moq) + 1;
      });
    }
  }

  function bindManual(input, moq) {
    input.addEventListener('change', function () {
      const v = parseInt(input.value, 10);
      if (isNaN(v) || v < moq) {
        showPdpErr(input, `Minimum order quantity is ${moq} unit${moq > 1 ? 's' : ''}.`);
        input.value = moq;
      } else {
        clearPdpErr(input);
      }
    });

    input.addEventListener('blur', function () {
      if (parseInt(input.value, 10) < moq) {
        input.value = moq;
        clearPdpErr(input);
      }
    });
  }

  function showPdpErr(input, msg) {
    clearPdpErr(input);
    const e = document.createElement('p');
    e.className = 'flexra-moq-error';
    e.textContent = msg;
    const qc = input.closest('.quantity__container');
    if (qc && qc.parentNode) qc.after(e);
  }

  function clearPdpErr(input) {
    const wrap = input.closest('.productView-quantity');
    if (wrap) wrap.querySelectorAll('.flexra-moq-error').forEach(el => el.remove());
  }


  /* ═══════════════════════════════════════════════════════
     3. VARIANT SWITCH — RE-CALC ON VARIANT CHANGE
  ═══════════════════════════════════════════════════════ */
  function bindVariantChange() {
    document.addEventListener('variant:changed', function (e) {
      const variant = e.detail && e.detail.variant;
      const input = document.querySelector('.productView-quantity .quantity__input');
      if (!input) return;

      const priceCents = variant ? variant.price : parseInt(input.dataset.price || '0', 10);
      input.dataset.price = priceCents;
      const moq = calcMOQ(priceCents);

      applyToInput(input, moq);
      renderBadge(input, moq);
      bindButtons(input, moq);
      bindManual(input, moq);
    });

    // Fallback: native radio/select change
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest('form[action*="/cart/add"] [name^="options"]')) {
        setTimeout(initProductPage, 300);
      }
    });
  }


  /* ═══════════════════════════════════════════════════════
     4. ADD-TO-CART — FORM SUBMIT INTERCEPT (CAPTURE PHASE)
  ═══════════════════════════════════════════════════════ */
  function bindATCValidation() {
    document.addEventListener('submit', function (e) {
      const form = e.target;
      if (!form || !form.action || !form.action.includes('/cart/add')) return;

      const input = form.querySelector('.quantity__input[data-moq]');
      if (!input) return;

      const moq = parseInt(input.dataset.moq, 10);
      const qty = parseInt(input.value, 10);

      if (isNaN(qty) || qty < moq) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showPdpErr(input, `This product requires a minimum order of ${moq} unit${moq > 1 ? 's' : ''}.`);
        input.value = moq;
        input.focus();
      }
    }, true);
  }


  /* ═══════════════════════════════════════════════════════
     5. FETCH PATCH — INTERCEPT /cart/add API CALL
        Enforces MOQ at the API level (bypass-proof)
  ═══════════════════════════════════════════════════════ */
  function patchCartAddFetch() {
    const _fetch = window.fetch;
    window.fetch = function (url, options) {
      try {
        if (
          typeof url === 'string' &&
          url.includes('/cart/add') &&
          options &&
          options.body
        ) {
          let body;
          try { body = JSON.parse(options.body); } catch (e) { body = null; }

          if (body && body.quantity !== undefined && body.id !== undefined) {
            const qtyInput = document.querySelector('.productView-quantity .quantity__input');
            if (qtyInput) {
              const priceCents = parseInt(qtyInput.dataset.price || '0', 10);
              const moq = calcMOQ(priceCents);
              if (body.quantity < moq) {
                body.quantity = moq;
                options = Object.assign({}, options, { body: JSON.stringify(body) });
              }
            }
          }
        }
      } catch (e) {}
      return _fetch.apply(this, [url, options]);
    };
  }


  /* ═══════════════════════════════════════════════════════
     6. CART PAGE + SIDE CART — VALIDATION & BUTTON LOCKS
  ═══════════════════════════════════════════════════════ */
  function initCartPage() {

    // ── Main cart page: checkout button ──────────────────
    const checkoutBtn = document.getElementById('cart-checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', function (e) {
        if (!validateMainCart()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
    }

    // ── Main cart page: form submit ───────────────────────
    const cartForm = document.querySelector('form[action="/cart"]');
    if (cartForm) {
      cartForm.addEventListener('submit', function (e) {
        if (!validateMainCart()) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      }, true);
    }

    // ── Side cart: checkout button ────────────────────────
    bindSideCartCheckout();

    // ── Minus buttons: main cart + side cart ─────────────
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('[data-minus-quantity-cart]');
      if (!btn) return;

      // Side cart row
      const sideRow = btn.closest('.previewCartItem');
      if (sideRow) {
        const inp     = sideRow.querySelector('.form-input.quantity');
        const priceEl = sideRow.querySelector('.previewCartItem-price[data-price]');
        if (!inp || !priceEl) return;
        const moq = calcMOQ(parseInt(priceEl.dataset.price || '0', 10));
        const cur = parseInt(inp.value, 10) || moq;
        if (cur - 1 < moq) {
          e.preventDefault();
          e.stopImmediatePropagation();
          showSideErr(sideRow, `Minimum quantity for this product is ${moq} unit${moq > 1 ? 's' : ''}.`);
          setTimeout(() => sideRow.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove()), 3000);
        }
        return;
      }

      // Main cart row
      const row = btn.closest('.cart-item');
      if (!row) return;
      const inp = row.querySelector('.cart-item-qty-input');
      const pc  = row.querySelector('.cart-item-total[data-price]');
      if (!inp || !pc) return;
      const moq = calcMOQ(parseInt(pc.dataset.price || '0', 10));
      const cur = parseInt(inp.value, 10) || moq;
      if (cur - 1 < moq) {
        e.preventDefault();
        e.stopImmediatePropagation();
        showMainCartErr(row, `Minimum quantity for this product is ${moq} unit${moq > 1 ? 's' : ''}.`);
        setTimeout(() => row.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove()), 3000);
      }
    }, true);
  }

  // Side cart checkout button bind (called on init + after AJAX re-render)
  function bindSideCartCheckout() {
    const btn = document.getElementById('cart-sidebar-checkout');
    if (!btn || btn.dataset.moqBound === '1') return;
    btn.dataset.moqBound = '1';

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopImmediatePropagation();
      if (validateSideCart()) {
        const root = (window.routes && window.routes.root) ? window.routes.root : '';
        window.location = root + '/checkout';
      }
    }, true);
  }


  /* ── Main cart validation ─────────────────────────────── */
  function validateMainCart() {
    document.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove());
    let valid = true;

    document.querySelectorAll('.cart-item').forEach(function (row) {
      const inp = row.querySelector('.cart-item-qty-input');
      const pc  = row.querySelector('.cart-item-total[data-price]');
      if (!inp || !pc) return;

      const moq = calcMOQ(parseInt(pc.dataset.price || '0', 10));
      const qty = parseInt(inp.value, 10);

      if (isNaN(qty) || qty < moq) {
        valid = false;
        inp.value = moq;
        showMainCartErr(row, `This product requires a minimum quantity of ${moq} unit${moq > 1 ? 's' : ''}.`);
      }
    });

    return valid;
  }

  /* ── Side cart validation ─────────────────────────────── */
  function validateSideCart() {
    document.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove());
    let valid = true;

    document.querySelectorAll('.previewCartItem').forEach(function (row) {
      const inp     = row.querySelector('.form-input.quantity');
      const priceEl = row.querySelector('.previewCartItem-price[data-price]');
      if (!inp || !priceEl) return;

      const moq = calcMOQ(parseInt(priceEl.dataset.price || '0', 10));
      const qty = parseInt(inp.value, 10);

      if (isNaN(qty) || qty < moq) {
        valid = false;
        inp.value = moq;
        showSideErr(row, `This product requires a minimum quantity of ${moq} unit${moq > 1 ? 's' : ''}.`);
      }
    });

    return valid;
  }

  /* ── Error helpers ────────────────────────────────────── */
  function showMainCartErr(row, msg) {
    row.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove());
    const e = document.createElement('p');
    e.className = 'flexra-cart-moq-error';
    e.textContent = msg;
    const qtyWrap = row.querySelector('.cart-item-qty');
    if (qtyWrap) qtyWrap.after(e);
  }

  function showSideErr(row, msg) {
    row.querySelectorAll('.flexra-cart-moq-error').forEach(el => el.remove());
    const e = document.createElement('p');
    e.className = 'flexra-cart-moq-error';
    e.textContent = msg;
    const qtyWrap = row.querySelector('.previewCartItem-qty');
    if (qtyWrap) qtyWrap.after(e);
  }


  /* ═══════════════════════════════════════════════════════
     7. MUTATION OBSERVER — SIDE CART CHECKOUT RE-BIND
        Re-binds checkout button after every AJAX re-render
  ═══════════════════════════════════════════════════════ */
  function watchSideCart() {
    const target =
      document.querySelector('.halo-sidebar-cart') ||
      document.querySelector('[data-side-cart]')   ||
      document.querySelector('.halo-popup-cart')   ||
      document.querySelector('#halo-side-cart')    ||
      document.querySelector('.previewCart');

    if (!target) return;

    const observer = new MutationObserver(function () {
      const btn = document.getElementById('cart-sidebar-checkout');
      if (btn) {
        delete btn.dataset.moqBound;
        bindSideCartCheckout();
      }
    });

    observer.observe(target, { childList: true, subtree: true });
  }


  /* ═══════════════════════════════════════════════════════
     8. SIDE CART AJAX RE-RENDER — AUTO-CORRECT QTY
        Watches DOM for new .previewCartItem nodes,
        enforces MOQ and syncs quantity to Shopify server
  ═══════════════════════════════════════════════════════ */
  function enforceSideCartQtyAfterRender() {
    const observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.addedNodes.length === 0) return;

        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;

          const items = node.classList && node.classList.contains('previewCartItem')
            ? [node]
            : Array.from(node.querySelectorAll ? node.querySelectorAll('.previewCartItem') : []);

          items.forEach(function (row) {
            const inp     = row.querySelector('.form-input.quantity');
            const priceEl = row.querySelector('.previewCartItem-price[data-price]');
            if (!inp || !priceEl) return;

            const priceCents = parseInt(priceEl.dataset.price || '0', 10);
            const moq        = calcMOQ(priceCents);
            const currentQty = parseInt(inp.value, 10);

            if (currentQty < moq) {
              inp.value = moq;

              // Sync corrected quantity to Shopify server
              const lineIndex = inp.dataset.index;
              if (lineIndex) {
                setTimeout(function () {
                  fetch(
                    (window.routes && window.routes.cart_change_url) || '/cart/change.js',
                    {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ line: parseInt(lineIndex, 10), quantity: moq })
                    }
                  )
                  .then(function (r) { return r.json(); })
                  .then(function (cart) {
                    // Update price displays if present
                    const subtotalEl = document.querySelector('[data-cart-subtotal]');
                    const totalEl    = document.querySelector('[data-cart-total-value]');
                    const fmt        = window.moneyFormat || '${{amount}}';

                    if (subtotalEl && cart.original_total_price !== undefined) {
                      subtotalEl.textContent = Shopify.formatMoney(cart.original_total_price, fmt);
                    }
                    if (totalEl && cart.total_price !== undefined) {
                      totalEl.textContent = Shopify.formatMoney(cart.total_price, fmt);
                    }
                  })
                  .catch(function () {});
                }, 150);
              }
            }
          });
        });
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }


  /* ═══════════════════════════════════════════════════════
     9. PUBLIC INIT
  ═══════════════════════════════════════════════════════ */
  function init() {
    // Must run first — patches fetch before any cart/add call
    patchCartAddFetch();

    // Product page
    if (document.querySelector('.productView-quantity')) {
      initProductPage();
      bindVariantChange();
      bindATCValidation();
    }

    // Main cart page OR side cart
    if (
      document.querySelector('.cart-item-qty-input') ||
      document.querySelector('.previewCartItem')     ||
      document.getElementById('cart-sidebar-checkout')
    ) {
      initCartPage();
    }

    // Always active — watch for AJAX re-renders
    watchSideCart();
    enforceSideCartQtyAfterRender();
  }

  // Expose for DevTools testing
  return { init, calcMOQ };

})();


/* ── Auto-init on DOM ready ─────────────────────────────── */
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', FlexraMOQ.init);
} else {
  FlexraMOQ.init();
}
